import { Input, Component } from '@angular/core';

import { take, filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { BriefingModel } from '../../models/briefing.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BriefingsService } from '../../services/briefings.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { validateEmails, emailSeparatorsRegex } from '../../../../Common/Misc/helpers/formatting/email.helper';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { InputValidationModel } from '../../../../Common/Forms/models/input-validation.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

enum PreviewModes {
    view,
    send
}

@Component({
    selector: 'agpr-briefing-preview-dialog',
    templateUrl: './briefing-preview-dialog.component.html',
    styleUrls: ['./briefing-preview-dialog.component.scss'],
    standalone: false
})
export class BriefingPreviewDialogComponent extends AbstractDialogComponent {

    @Input() briefing: BriefingModel = null;

    modes = PreviewModes;
    mode: PreviewModes = PreviewModes.view;

    recipients: string[] = [];
    showRecepientListError: boolean = false;
    loading: boolean = false;

    readonly messageSeverity = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly splitOnPattern: RegExp = emailSeparatorsRegex;
    readonly validateEmails: InputValidationModel[] = [
        new InputValidationModel({
            errorKey: 'invalidEmailFormat',
            isValid: validateEmails
        })
    ];

    constructor(
        private localizationService: LocalizationService,
        private briefingsService: BriefingsService,
        private messageService: MessageService,
        public coreDialogService: CoreDialogService,
        private userStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'BriefingPreview');
        this.userStateService.getUser().pipe(
            filter(val => !!val),
            take(1),
        ).subscribe(user => this.recipients = [user.email]);
    }

    prepBriefingForPreview(): BriefingModel {
        const briefing = this.briefing.clone();
        const subjectLineEmpty: boolean = !briefing.subject || briefing.subject.length === 0;
        const fromNameEmpty: boolean = !briefing.fromName || briefing.fromName.length === 0;

        // set default subject line / from name if necessary
        if (subjectLineEmpty) {
            briefing.subject = this.localizationService.transform('Briefing.preview.subjectLine');
        }

        if (fromNameEmpty) {
            briefing.fromName = this.localizationService.transform('Briefing.preview.fromName');
        }

        return briefing;
    }

    /**
     * Sends the current briefing as a preview
     */
    sendPreview(): void {
        if (this.recipients && this.recipients.length === 0) {
            this.showRecepientListError = true;
            return;
        }

        const previewBriefing = this.prepBriefingForPreview();
        previewBriefing.recipientList = this.recipients;

        this.loading = true;
        this.briefingsService.sendPreview(previewBriefing.toApiModel()).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Briefing.preview.send.success.heading'),
                    detail: this.localizationService.transform('Briefing.preview.send.success.message')
                });
                this.closeDialog();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Briefing.preview.send.error.heading'),
                    detail: this.localizationService.transform('Briefing.preview.send.error.message')
                });
            }
        });
    }

    viewPreview(): void {
        const previewBriefing = this.prepBriefingForPreview();

        this.loading = true;
        this.briefingsService.openPreview(previewBriefing.toApiModel()).subscribe({
            next: () => {
                this.loading = false;
                this.closeDialog();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    // TODO: Update messages for view instead of send
                    summary: 'Briefing.preview.view.error.heading',
                    detail: 'Briefing.preview.view.error.message'
                });
            }
        });
    }

    confirm(): void {
        if (this.mode === PreviewModes.send) {
            this.sendPreview();
        } else {
            this.viewPreview();
        }
    }
}
